import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Star, Sparkles, MousePointer2, ChevronDown } from 'lucide-react';
import { personalInfo, projectsData, skillsData, testimonialsData } from '../data/mock';

const HomePage = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    // Intersection Observer for animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      observer.disconnect();
    };
  }, []);

  const featuredProjects = projectsData.filter((p) => p.featured).slice(0, 3);
  const featuredTestimonials = testimonialsData.slice(0, 3);

  return (
    <div className="relative">
      {/* Animated Background */}
      <div className="gradient-bg" />
      <div className="grid-bg" />
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section ref={heroRef} className="min-h-screen flex items-center relative pt-32 pb-20 overflow-hidden">
        {/* Floating Shapes */}
        <div 
          className="absolute top-1/4 right-1/4 w-72 h-72 bg-gradient-to-br from-[#00ff88]/20 to-transparent rounded-full blur-3xl morph-shape"
          style={{ transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)` }}
        />
        <div 
          className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-[#7c3aed]/10 to-transparent rounded-full blur-3xl"
          style={{ transform: `translate(${mousePosition.x * -0.01}px, ${mousePosition.y * -0.01}px)` }}
        />

        {/* Decorative Elements */}
        <div className="absolute top-40 left-10 text-[#00ff88]/20 float-animation" style={{ animationDelay: '0s' }}>
          <Sparkles size={40} />
        </div>
        <div className="absolute top-60 right-20 text-[#7c3aed]/30 float-animation" style={{ animationDelay: '2s' }}>
          <MousePointer2 size={30} />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full relative z-10">
          <div className="max-w-5xl">
            {/* Status Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
              style={{ animation: 'fadeUp 0.8s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00ff88] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00ff88]"></span>
              </span>
              <span className="text-sm text-white/70">Available for new projects</span>
            </div>

            {/* Main Headline */}
            <h1 className="mb-8">
              <div className="overflow-hidden">
                <span 
                  className="block text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-bold text-white leading-[0.9] tracking-tight"
                  style={{ animation: 'fadeUp 1s 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(100%)' }}
                >
                  I craft
                </span>
              </div>
              <div className="overflow-hidden">
                <span 
                  className="block text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-bold leading-[0.9] tracking-tight"
                  style={{ animation: 'fadeUp 1s 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(100%)' }}
                >
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff88] via-[#00cc6a] to-[#7c3aed] glow-text">digital</span>
                </span>
              </div>
              <div className="overflow-hidden">
                <span 
                  className="block text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-bold text-white leading-[0.9] tracking-tight"
                  style={{ animation: 'fadeUp 1s 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(100%)' }}
                >
                  experiences
                </span>
              </div>
            </h1>

            {/* Subtitle */}
            <p 
              className="text-xl lg:text-2xl text-white/50 max-w-2xl leading-relaxed mb-12"
              style={{ animation: 'fadeUp 1s 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              {personalInfo.heroDescription}
            </p>

            {/* CTA Buttons */}
            <div 
              className="flex flex-wrap gap-4 mb-16"
              style={{ animation: 'fadeUp 1s 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              <Link to="/work">
                <button className="group relative px-8 py-4 bg-[#00ff88] text-black rounded-full font-semibold text-lg overflow-hidden transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,255,136,0.4)]">
                  <span className="relative z-10 flex items-center gap-2">
                    View My Work
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </Link>
              <Link to="/contact">
                <button className="group px-8 py-4 bg-transparent border-2 border-white/20 text-white rounded-full font-semibold text-lg hover:border-[#00ff88] hover:text-[#00ff88] transition-all duration-300">
                  <span className="flex items-center gap-2">
                    Let's Talk
                    <ArrowUpRight size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </span>
                </button>
              </Link>
            </div>

            {/* Stats */}
            <div 
              className="flex flex-wrap gap-12 lg:gap-20"
              style={{ animation: 'fadeUp 1s 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              {[
                { value: '7+', label: 'Years Experience' },
                { value: '50+', label: 'Projects Delivered' },
                { value: '30+', label: 'Happy Clients' },
              ].map((stat, index) => (
                <div key={index} className="group cursor-default">
                  <p className="text-5xl lg:text-6xl font-bold text-white group-hover:text-[#00ff88] transition-colors duration-300">
                    {stat.value}
                  </p>
                  <p className="text-white/40 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30">
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown size={20} className="scroll-indicator" />
        </div>
      </section>

      {/* Marquee Section */}
      <section className="py-12 border-y border-white/5 overflow-hidden">
        <div className="marquee">
          <div className="marquee-content flex items-center gap-12 text-4xl lg:text-5xl font-bold text-white/10">
            {['UI DESIGN', '•', 'UX RESEARCH', '•', 'PROTOTYPING', '•', 'DESIGN SYSTEMS', '•', 'WEB DESIGN', '•', 'MOBILE APPS', '•',
              'UI DESIGN', '•', 'UX RESEARCH', '•', 'PROTOTYPING', '•', 'DESIGN SYSTEMS', '•', 'WEB DESIGN', '•', 'MOBILE APPS', '•'].map((item, i) => (
              <span key={i} className="whitespace-nowrap">{item}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="reveal-on-scroll stagger-children flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-4">
                Selected Work
              </span>
              <h2 className="text-5xl lg:text-6xl font-bold text-white">
                Featured<br />Projects
              </h2>
            </div>
            <Link
              to="/work"
              className="group flex items-center gap-3 text-white/60 hover:text-[#00ff88] transition-colors"
            >
              <span className="text-lg">View All Projects</span>
              <div className="w-12 h-12 rounded-full border border-current flex items-center justify-center group-hover:bg-[#00ff88] group-hover:border-[#00ff88] group-hover:text-black transition-all">
                <ArrowRight size={20} />
              </div>
            </Link>
          </div>

          <div className="space-y-8">
            {featuredProjects.map((project, index) => (
              <Link
                key={project.id}
                to={`/work/${project.id}`}
                className="reveal-on-scroll stagger-children group block"
              >
                <div className="glass-card p-4 lg:p-6 transition-all duration-500 hover:scale-[1.02]">
                  <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center">
                    {/* Image */}
                    <div className={`relative aspect-[16/10] rounded-2xl overflow-hidden ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <img
                        src={project.thumbnail}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0">
                        <span className="text-white font-medium">View Case Study</span>
                        <div className="w-12 h-12 rounded-full bg-[#00ff88] flex items-center justify-center text-black">
                          <ArrowUpRight size={20} />
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-6 p-4">
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium">
                          {project.category}
                        </span>
                        <span className="text-white/40">{project.year}</span>
                      </div>
                      <h3 className="text-3xl lg:text-4xl font-bold text-white group-hover:text-[#00ff88] transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-white/50 text-lg leading-relaxed line-clamp-2">
                        {project.problem}
                      </p>
                      <div className="flex flex-wrap gap-2 pt-4">
                        {project.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-4 py-2 rounded-full bg-white/5 text-white/60 text-sm border border-white/10"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#00ff88]/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="reveal-on-scroll stagger-children text-center mb-20">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#7c3aed]/10 text-[#7c3aed] text-sm font-medium mb-4">
              Services
            </span>
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              What I Do
            </h2>
            <p className="text-xl text-white/50 max-w-2xl mx-auto">
              Transforming ideas into exceptional digital experiences through research, design, and iteration.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skillsData.services.map((service, index) => (
              <div
                key={service.title}
                className="reveal-on-scroll stagger-children group glass-card p-8 hover:-translate-y-2 transition-all duration-500"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00ff88]/20 to-[#7c3aed]/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Sparkles className="w-7 h-7 text-[#00ff88]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#00ff88] transition-colors">
                  {service.title}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <button className="group px-8 py-4 bg-transparent border-2 border-white/20 text-white rounded-full font-semibold hover:border-[#00ff88] hover:text-[#00ff88] transition-all duration-300">
                <span className="flex items-center gap-2">
                  View All Services
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="reveal-on-scroll stagger-children flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-4">
                Testimonials
              </span>
              <h2 className="text-5xl lg:text-6xl font-bold text-white">
                Client<br />Reviews
              </h2>
            </div>
            <Link
              to="/testimonials"
              className="group flex items-center gap-3 text-white/60 hover:text-[#00ff88] transition-colors"
            >
              <span className="text-lg">All Reviews</span>
              <div className="w-12 h-12 rounded-full border border-current flex items-center justify-center group-hover:bg-[#00ff88] group-hover:border-[#00ff88] group-hover:text-black transition-all">
                <ArrowRight size={20} />
              </div>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {featuredTestimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="reveal-on-scroll stagger-children group glass-card p-8 hover:-translate-y-2 transition-all duration-500"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[#00ff88] text-[#00ff88]" />
                  ))}
                </div>
                <p className="text-white/80 text-lg leading-relaxed mb-8">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-4 pt-6 border-t border-white/10">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-[#00ff88]/20"
                  />
                  <div>
                    <p className="text-white font-semibold">{testimonial.author}</p>
                    <p className="text-white/40 text-sm">
                      {testimonial.role}, {testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="reveal-on-scroll stagger-children relative glass-card p-12 lg:p-20 text-center overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#00ff88]/20 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-[#7c3aed]/20 to-transparent rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-6">
                Let's Collaborate
              </span>
              <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6">
                Ready to bring your<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff88] to-[#7c3aed]">vision to life?</span>
              </h2>
              <p className="text-xl text-white/50 mb-10 max-w-2xl mx-auto">
                Let's discuss your project and see how we can create something amazing together.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <button className="group px-10 py-5 bg-[#00ff88] text-black rounded-full font-semibold text-lg hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300">
                    <span className="flex items-center gap-2">
                      Start a Project
                      <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Link>
                <a href={`mailto:${personalInfo.email}`}>
                  <button className="px-10 py-5 bg-white/5 text-white rounded-full font-semibold text-lg border border-white/10 hover:bg-white/10 transition-all duration-300">
                    {personalInfo.email}
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
